/// ============================================================
// src/pages/Register.tsx
// ============================================================
import React, { useState, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link as MuiLink,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff, Apple } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import AppleSignin from "react-apple-signin-auth";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useAuth } from "../../hooks/useAuth";
import logo from '../../assets/logo.png'

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const schema = z
  .object({
    email: z.string().email("Invalid email"),
    password: z.string().min(6, "Minimum 6 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register: registerUser, socialLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    turnstileRef.current?.reset();
  }, []);

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) {
      setError("No credential received from Google");
      return;
    }

    setSocialLoading(true);
    setError(null);

    try {
      await socialLogin({
        provider: "google",
        token: response.credential,
        turnstileToken: turnstileToken || undefined,
      });
      // New social users go to onboarding
      navigate("/onboarding/plan");
    } catch (err: any) {
      setError(err.response?.data?.message || "Google sign-up failed. Please try again.");
      resetTurnstile();
      setSocialLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-up was cancelled or failed.");
  };

  const handleAppleSuccess = async (response: any) => {
    const idToken = response.authorization?.id_token;
    if (!idToken) {
      setError("No credential received from Apple");
      return;
    }

    setSocialLoading(true);
    setError(null);

    try {
      await socialLogin({
        provider: "apple",
        token: idToken,
        // Apple only provides name on first sign-in, pass it if available
        firstName: response.user?.name?.firstName,
        lastName: response.user?.name?.lastName,
        turnstileToken: turnstileToken || undefined,
      });
      // New social users go to onboarding
      navigate("/onboarding/plan");
    } catch (err: any) {
      setError(err.response?.data?.message || "Apple sign-up failed. Please try again.");
      resetTurnstile();
      setSocialLoading(false);
    }
  };

  const handleAppleError = (error: any) => {
    // User cancelled - don't show error
    if (error?.error === "popup_closed_by_user") {
      return;
    }
    setError("Apple sign-up was cancelled or failed.");
  };

  const turnstileReady = !TURNSTILE_SITE_KEY || !!turnstileToken;
  const isAnyLoading = loading || socialLoading;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);

    const { email, password } = data;
    try {
      await registerUser({
        email,
        password,
        turnstileToken: turnstileToken || undefined,
      });
      navigate("/onboarding/plan");
    } catch (err: any) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
      resetTurnstile();
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={400} mx="auto" mt={8}>
      <Box textAlign="center" mb={2}>
        <img src={logo} alt="Company logo" style={{ height: 120 }} />
      </Box>

      <Typography variant="h4" gutterBottom>
        Sign up
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Social Sign-Up Buttons */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mb: 2 }}>
        {socialLoading ? (
          <CircularProgress size={40} />
        ) : (
          <>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              size="large"
              width="350"
              text="signup_with"
            />
            {APPLE_CLIENT_ID && (
              <AppleSignin
                authOptions={{
                  clientId: APPLE_CLIENT_ID,
                  scope: "email name",
                  redirectURI: window.location.origin,
                  usePopup: true,
                }}
                uiType="dark"
                onSuccess={handleAppleSuccess}
                onError={handleAppleError}
                render={(props: any) => (
                  <Button
                    {...props}
                    variant="outlined"
                    startIcon={<Apple />}
                    sx={{
                      width: 350,
                      height: 40,
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#000",
                      borderColor: "#000",
                      backgroundColor: "#fff",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                        borderColor: "#000",
                      },
                    }}
                  >
                    Sign up with Apple
                  </Button>
                )}
              />
            )}
          </>
        )}
      </Box>

      <Divider sx={{ my: 2 }}>or sign up with email</Divider>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          label="Email"
          fullWidth
          margin="normal"
          autoComplete="email"
          disabled={isAnyLoading}
          {...register("email")}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          autoComplete="new-password"
          disabled={isAnyLoading}
          {...register("password")}
          error={!!errors.password}
          helperText={errors.password?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          autoComplete="new-password"
          disabled={isAnyLoading}
          {...register("confirmPassword")}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {TURNSTILE_SITE_KEY && (
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            options={{ size: "invisible" }}
            onSuccess={setTurnstileToken}
            onExpire={resetTurnstile}
          />
        )}
                <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 2 }}
          disabled={isAnyLoading || !turnstileReady}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Create account with Email"}
        </Button>
      </form>
      <Typography variant="body2" align="center" sx={{ mt: 2 }}>
        Already have an account? <MuiLink component={Link} to="/login">Log in</MuiLink>
      </Typography>
    </Box>
  );
};

export default Register;